import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";

/**
 * OAuth return target for the native Android shell.
 *
 * Google sign-in inside the app completes in the system browser; the broker
 * then redirects here. An Android App Link on /auth/callback hands that URL
 * back to the installed app, where this page waits for the session to hydrate
 * (the Supabase client picks up the tokens from the URL automatically) and
 * forwards to the intended page. On the web this route is harmless — it
 * behaves like /login's post-sign-in forwarder.
 */
export const Route = createFileRoute("/auth/callback")({
  component: AuthCallbackPage,
  validateSearch: (s: Record<string, unknown>): { next?: string } => ({
    next: typeof s.next === "string" ? s.next : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Completing sign-in — MissionReady" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function AuthCallbackPage() {
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const { session, memberships, loading, membershipsLoading } = useAuth();
  // If no session shows up (e.g. someone opens the link by hand), offer a way out.
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (loading || membershipsLoading) return;
    if (session) {
      const safe = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
      navigate({ to: safe ?? (memberships.length ? "/operations" : "/onboarding"), replace: true });
    }
  }, [session, memberships.length, loading, membershipsLoading, navigate, next]);

  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 10000);
    return () => clearTimeout(t);
  }, []);

  return (
    <main className="flex min-h-dvh items-center justify-center bg-hero px-4">
      <div className="w-full max-w-md text-center">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-card">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden />
          <h1 className="font-display text-xl font-semibold">Completing sign-in…</h1>
          <p className="mt-1 text-sm text-muted-foreground">Hold on while we finish signing you in.</p>
          {timedOut && !session && (
            <p className="mt-4 text-sm text-muted-foreground">
              This is taking longer than expected.{" "}
              <Link to="/login" className="text-primary underline underline-offset-2">
                Return to sign in
              </Link>
            </p>
          )}
        </div>
      </div>
    </main>
  );
}
